import React, { useEffect, useState, useCallback, memo } from "react";
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { Chart } from "react-google-charts";
import { api } from '../../services/api.js';

// 1. TIMER ISOLADO - Crucial para a tela não "pular" a cada segundo
const TVTimer = memo(({ segundos }) => {
    return (
        <div style={{
            position: 'fixed', bottom: '15px', right: '15px', zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.8)', color: '#fff', padding: '8px 15px',
            borderRadius: '30px', border: '1px solid #0d6efd', fontSize: '0.85rem',
            fontFamily: 'monospace'
        }}>
            REFRESH EM: {Math.floor(segundos / 60)}:{String(segundos % 60).padStart(2, '0')}
        </div>
    );
});

function DashboardDeosTecnico() {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [segundos, setSegundos] = useState(180);

    const carregarDados = useCallback(async () => {
        try {
            const res = await api.get('/chamados/dashboard');
            setDados(res.data);
            setSegundos(180);
        } catch (err) {
            console.error("Erro Arbor:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        carregarDados();
        const apiInt = setInterval(carregarDados, 180000);
        const timerInt = setInterval(() => setSegundos(s => (s <= 1 ? 180 : s - 1)), 1000);
        return () => { clearInterval(apiInt); clearInterval(timerInt); };
    }, [carregarDados]);

    if (loading && !dados) return (
        <div className="vh-100 d-flex justify-content-center align-items-center bg-dark text-white">
            <Spinner animation="border" variant="primary" className="me-3" />
            <h3>CARREGANDO MONITORAMENTO...</h3>
        </div>
    );

    const resumo = dados?.resumo[0] || {};
    const tempoMedio = dados?.tempo[0] || 0;

    // Opções dos Gráficos de Barras/Colunas
    const barOptions = (color) => ({
        legend: { position: "none" },
        chartArea: { width: '90%', height: '70%' },
        colors: [color],
        animation: { duration: 0 },
        vAxis: { minValue: 0 },
        hAxis: { textStyle: { fontSize: 10, fontWeight: 'bold' } }
    });

    // Opções do Gráfico de Pizza (Categorias)
    const pieOptions = {
        legend: { position: "right", textStyle: { fontSize: 11 } },
        chartArea: { width: '95%', height: '80%' },
        pieHole: 0.4, // Estilo Donut para ficar mais moderno na TV
        is3D: false,
        colors: ['#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'],
    };

    // Preparação dos dados baseada no seu JSON
    const dataLojasHoje = [["Loja", "Qtd", { role: "annotation" }],
    ...dados.rankinglojas.map(l => [`L${l.loja}`, l.hoje, l.hoje])];

    const dataLojasMes = [["Loja", "Qtd", { role: "annotation" }],
    ...dados.rankinglojas.map(l => [`L${l.loja}`, l.dias, l.dias])];

    const dataCategorias = [["Categoria", "Total"],
    ...dados.categoria.slice(0, 10).map(c => [c.descricao, c.total])];

    // No mapeamento dos dados dos técnicos:
    const barTecnicosOptions = {
        legend: { position: "none" },
        chartArea: {
            width: '75%', // Diminuí a largura do gráfico para sobrar espaço na esquerda
            height: '80%',
            left: 100    // Aumentei o recuo para o nome caber por extenso
        },
        hAxis: { minValue: 0 },
        vAxis: { textStyle: { fontSize: 12, fontWeight: 'bold' } },
        animation: { duration: 0 },
    };
    const dataTecnicos = [
        ["Usuário", "Total", { role: "style" }, { role: "annotation" }],
        ...dados.ranking.map((u, idx) => [
            u.nome.split(' ')[0], // Mantém o primeiro nome
            u.total,
            idx === 0 ? '#0d6efd' : '#6610f2', // Alterna cores entre os técnicos
            u.total
        ])
    ];

    return (
        <div style={{ height: '100vh', width: '100vw', overflow: 'hidden', backgroundColor: '#f8f9fa', position: 'fixed', top: 0, left: 0 }}>

            {/* Header - 8% altura */}
            <div className="bg-white border-bottom d-flex justify-content-between align-items-center px-4 shadow-sm" style={{ height: '8vh' }}>
                <div className="d-flex align-items-center">
                    <div className="bg-primary rounded-circle me-2" style={{ width: '15px', height: '15px' }}></div>
                    <h4 className="fw-bold mb-0 text-dark">ARBOR DASHBOARD TÉCNICO</h4>
                </div>
                <h3 className="fw-bold mb-0 text-secondary">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</h3>
            </div>

            <Container fluid style={{ height: '92vh' }} className="p-3">
                {/* KPIs - 15% altura */}
                <Row className="g-2 mb-3" style={{ height: '15%' }}>
                    {[
                        { label: 'ABERTOS', val: resumo.abertos, col: 'primary' },
                        { label: 'EM ATENDIMENTO', val: resumo.atendimento, col: 'warning' },
                        { label: 'AGUARDANDO', val: resumo.aguardando, col: 'info' },
                        { label: 'FECHADOS (HOJE)', val: resumo.fechados, col: 'success' },
                        { label: 'TMA MÉDIO', val: `${tempoMedio.toFixed(1)}h`, col: 'dark' }
                    ].map((item, idx) => (
                        <Col key={idx}>
                            <Card className={`h-100 shadow-sm border-0 border-start border-4 border-${item.col}`}>
                                <Card.Body className="p-2 text-center d-flex flex-column justify-content-center">
                                    <span className="fw-bold text-muted small">{item.label}</span>
                                    <h1 className="fw-bold mb-0" style={{ fontSize: '2.2rem' }}>{item.val}</h1>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Grade de Gráficos - 77% altura */}
                <Row className="g-3" style={{ height: '77%' }}>
                    {/* Linha 1 */}
                    <Col md={6} className="h-50">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 fw-bold py-2 text-danger text-center">LOJAS: DEMANDA HOJE</Card.Header>
                            <Card.Body className="p-0">
                                <Chart chartType="ColumnChart" width="100%" height="100%" data={dataLojasHoje} options={barOptions('#dc3545')} />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} className="h-50">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 fw-bold py-2 text-success text-center">LOJAS: ÚLTIMOS 30 DIAS</Card.Header>
                            <Card.Body className="p-0">
                                <Chart chartType="ColumnChart" width="100%" height="100%" data={dataLojasMes} options={barOptions('#198754')} />
                            </Card.Body>
                        </Card>
                    </Col>
                    {/* Linha 2 */}
                    <Col md={6} className="h-50">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 fw-bold py-2 text-center text-dark">TOP CATEGORIAS (DISTRIBUIÇÃO)</Card.Header>
                            <Card.Body className="p-0">
                                <Chart chartType="PieChart" width="100%" height="100%" data={dataCategorias} options={pieOptions} />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} className="h-50">
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 fw-bold py-2 text-center text-primary">
                                RANKING PRODUTIVIDADE
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Chart
                                    chartType="BarChart"
                                    width="100%"
                                    height="100%"
                                    data={[
                                        ["Técnico", "Total", { role: "style" }, { role: "annotation" }],
                                        ...dados.ranking.map((u, idx) => {
                                            const cores = ['#0d6efd', '#6610f2', '#6f42c1', '#d63384'];
                                            return [
                                                u.nome.split(' ')[0], // Ou use u.nome se quiser o nome completo
                                                u.total,
                                                cores[idx % cores.length],
                                                u.total
                                            ];
                                        })
                                    ]}
                                    options={{
                                        legend: { position: "none" },
                                        chartArea: { width: '70%', height: '80%', left: 120 }, // 'left: 120' garante o nome
                                        hAxis: { minValue: 0 },
                                        vAxis: {
                                            textStyle: { fontSize: 13, fontWeight: 'bold' }
                                        }
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <TVTimer segundos={segundos} />
        </div>
    );
}

export default DashboardDeosTecnico;